/**
 * Warning confirmation dialog — the Import/Export Flow V2 popups
 * (Duplicate File, Cancel Upload, "this will update your data").
 *
 * Figma's shape: a featured warning icon (yellow alert glyph in an
 * outlined rounded square) top-left, an X top-right, title +
 * supporting copy, optional body (e.g. radios), and a footer whose
 * actions stretch edge to edge.
 */
import type { ReactNode } from "react";
import {
    Dialog as AriaDialog,
    Heading as AriaHeading,
    Modal as AriaModal,
    ModalOverlay as AriaModalOverlay,
} from "react-aria-components";
import { AlertCircle, XClose } from "@untitledui/icons";

export function WarningDialog({
    isOpen,
    onClose,
    title,
    description,
    children,
    actions,
    maxWidth = 400,
    "data-autotest": dataAutotest,
}: {
    isOpen: boolean;
    /** Fired by the X, the overlay click and Esc. */
    onClose: () => void;
    title: string;
    description: string;
    /** Optional body between the copy and the actions (radios, etc.). */
    children?: ReactNode;
    /** Footer buttons — stretched to share the row, per the Figma. */
    actions: ReactNode;
    /** Figma's modal is 400px. Callers with a long single-line title
     *  (which must not wrap) widen it a step rather than shrinking the
     *  type. */
    maxWidth?: number;
    /** Custom attribute for E2E testing — applied to the dialog itself. */
    "data-autotest"?: string;
}) {
    return (
        <AriaModalOverlay
            isOpen={isOpen}
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
            isDismissable
            className="bg-overlay/70 fixed inset-0 z-50 flex items-center justify-center p-4"
        >
            {/* Figma: Fill 400px / radius-2xl. */}
            <AriaModal style={{ maxWidth }} className="bg-primary outline-hidden w-full rounded-2xl shadow-2xl">
                <AriaDialog data-autotest={dataAutotest} className="outline-hidden flex flex-col gap-4 p-6">
                    <div className="flex items-start justify-between">
                        {/* Featured icon — outlined rounded square with the
                          * yellow alert glyph (Untitled UI "featured icon
                          * outline", same yellow as the nav warnings). */}
                        <span className="border-secondary shadow-xs flex size-11 items-center justify-center rounded-xl border">
                            <AlertCircle aria-hidden="true" className="text-utility-yellow-500 size-5" />
                        </span>
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={onClose}
                            className="text-fg-quaternary hover:text-fg-secondary -m-1 cursor-pointer rounded p-1"
                        >
                            <XClose aria-hidden="true" className="size-5" />
                        </button>
                    </div>
                    <div className="flex flex-col gap-1">
                        {/* `slot="title"` is what labels the dialog for screen
                          * readers. A plain h2 renders identically but leaves
                          * react-aria warning that the dialog is unnamed. */}
                        <AriaHeading slot="title" className="text-primary text-md font-semibold leading-6">
                            {title}
                        </AriaHeading>
                        <p className="text-tertiary text-sm">{description}</p>
                    </div>
                    {children}
                    <div className="mt-2 flex gap-3 [&>*]:flex-1">{actions}</div>
                </AriaDialog>
            </AriaModal>
        </AriaModalOverlay>
    );
}
